// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
import * as Dialog from '@wandb/weave/components/Dialog';
import {WaveLoader} from '@wandb/weave/components/Loaders/WaveLoader';
import React, {useCallback, useContext, useEffect, useState} from 'react';
import {toast} from 'react-toastify';

import {
  Tag,
  useAddProjectProtectedAliasesMutation,
  useArtifactMembershipsWithAliasCountQuery,
  useProjectProtectedAliasesQuery,
  useRemoveProjectProtectedAliasesMutation,
} from '../../../generated/graphql';
import {propagateErrorsContext} from '../../../util/errors';
import {ProtectedAliases} from '../common/ProtectedAliases';
import {globalRegistryType} from '../common/utils';
import {RegistryMembersContext} from './RegistryMembersContextProvider';

type RegistryProtectedAliasesProps = {
  registry: globalRegistryType;
};

enum ProtectedAliasAction {
  Add = 'add',
  Delete = 'delete',
}

interface AliasOperationState {
  action: ProtectedAliasAction;
  alias: string;
  artifactMembershipCount?: number;
}

export const RegistryProtectedAliases = ({
  registry,
}: RegistryProtectedAliasesProps) => {
  const [open, setOpen] = useState(false);
  const [aliasOperationState, setAliasOperationState] = useState<
    AliasOperationState | undefined
  >(undefined);
  const {isViewerRegistryAdmin} = useContext(RegistryMembersContext);

  const [addAliases, {loading: isAddingAliases}] =
    useAddProjectProtectedAliasesMutation();
  const [deleteAliases, {loading: isDeletingAliases}] =
    useRemoveProjectProtectedAliasesMutation();
  const {data: protectedAliases, refetch} = useProjectProtectedAliasesQuery({
    variables: {
      entityName: registry.entityName,
      projectName: registry.registryProjectName,
    },
  });

  const onAddAlias = useCallback(
    async (alias: string) => {
      if (!isViewerRegistryAdmin) {
        toast('You must be a registry admin to add protected aliases.');
        return;
      }
      try {
        await addAliases({
          variables: {
            entityName: registry.entityName,
            projectName: registry.registryProjectName,
            aliases: [alias],
          },
          context: propagateErrorsContext(),
        });
        await refetch();
        setOpen(false);
        setAliasOperationState(undefined);
        toast('Successfully added protected alias.');
      } catch (e) {
        console.error(e);
        toast('Something went wrong when adding this protected alias.');
      }
    },
    [
      isViewerRegistryAdmin,
      addAliases,
      registry.entityName,
      registry.registryProjectName,
      refetch,
    ]
  );

  const onDeleteAlias = React.useCallback(
    async (alias: string) => {
      if (!isViewerRegistryAdmin) {
        toast('You must be a registry admin to delete protected aliases.');
        return;
      }
      if (!alias) {
        toast('You must enter an alias to delete.');
        return;
      }
      try {
        await deleteAliases({
          variables: {
            entityName: registry.entityName,
            projectName: registry.registryProjectName,
            aliases: [alias],
          },
          context: propagateErrorsContext(),
        });
        await refetch();
        setOpen(false);
        setAliasOperationState(undefined);
        toast('Successfully deleted protected alias');
      } catch (e) {
        console.error(e);
        toast('Something went wrong when deleting this protected alias');
      }
    },
    [
      deleteAliases,
      registry.entityName,
      registry.registryProjectName,
      isViewerRegistryAdmin,
      refetch,
    ]
  );

  const onAddAliasClick = useCallback(async (tag: Tag) => {
    setAliasOperationState({
      action: ProtectedAliasAction.Add,
      alias: tag.name,
    });
  }, []);

  const onDeleteAliasClick = useCallback(async (alias: string) => {
    setAliasOperationState({
      action: ProtectedAliasAction.Delete,
      alias,
    });
  }, []);

  const {data: artifactMembershipsWithAliasCount} =
    useArtifactMembershipsWithAliasCountQuery({
      skip: !isViewerRegistryAdmin || !aliasOperationState,
      variables: {
        entityName: registry.entityName,
        projectName: registry.registryProjectName,
        alias: aliasOperationState?.alias ?? '',
      },
      // need to trigger a re-render after each query to get the latest artifact membership count
      notifyOnNetworkStatusChange: true,
    });

  useEffect(() => {
    if (artifactMembershipsWithAliasCount) {
      setAliasOperationState(prev => {
        if (!prev) {
          return undefined;
        }
        return {
          ...prev,
          artifactMembershipCount:
            artifactMembershipsWithAliasCount?.project
              ?.artifactMembershipsWithAliasCount ?? 0,
        };
      });
    }
  }, [artifactMembershipsWithAliasCount]);

  useEffect(() => {
    // wait for the artifact membership count to be set before taking action
    if (
      !aliasOperationState ||
      aliasOperationState.artifactMembershipCount === undefined
    ) {
      return;
    }
    if (aliasOperationState.artifactMembershipCount > 0) {
      setOpen(true);
      return;
    }
    if (aliasOperationState.action === ProtectedAliasAction.Add) {
      onAddAlias(aliasOperationState.alias);
    } else if (aliasOperationState.action === ProtectedAliasAction.Delete) {
      onDeleteAlias(aliasOperationState.alias);
    }
  }, [aliasOperationState, onAddAlias, onDeleteAlias]);

  return (
    <div>
      <ProtectedAliases
        aliases={
          protectedAliases?.project?.protectedArtifactAliases.map(
            alias => alias.alias
          ) ?? []
        }
        onAddAlias={onAddAliasClick}
        onDeleteAlias={isViewerRegistryAdmin ? onDeleteAliasClick : undefined}
        isViewerRegistryAdmin={isViewerRegistryAdmin}
        isGlobalRegistry={true}
      />
      {/* Only show the confirm modal if there are existing artifact memberships with the alias */}
      {aliasOperationState?.artifactMembershipCount !== undefined &&
        aliasOperationState.artifactMembershipCount > 0 && (
          <ProtectedAliasesConfirmModal
            open={open}
            setIsOpen={setOpen}
            onConfirm={
              aliasOperationState.action === ProtectedAliasAction.Add
                ? () => onAddAlias(aliasOperationState.alias)
                : () => onDeleteAlias(aliasOperationState.alias)
            }
            onCancel={() => {
              setOpen(false);
              setAliasOperationState(undefined);
            }}
            isLoading={isAddingAliases || isDeletingAliases}
            action={aliasOperationState.action}
            alias={aliasOperationState.alias}
            artifactMembershipCount={
              aliasOperationState.artifactMembershipCount
            }
          />
        )}
    </div>
  );
};

type ProtectedAliasesConfirmModalProps = {
  open: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onConfirm: () => void;
  isLoading: boolean;
  action: ProtectedAliasAction;
  alias: string;
  artifactMembershipCount: number;
  onCancel: () => void;
};

const ProtectedAliasesConfirmModal = ({
  open,
  setIsOpen,
  onConfirm,
  action,
  alias,
  isLoading,
  artifactMembershipCount,
  onCancel,
}: ProtectedAliasesConfirmModalProps) => {
  return (
    <Dialog.Root open={open} onOpenChange={setIsOpen}>
      <Dialog.Portal>
        <Dialog.Overlay />
        <Dialog.Content
          className="w-[540px] rounded-lg"
          onInteractOutside={e => {
            e.preventDefault();
          }}>
          <Dialog.Title>
            Transform existing{' '}
            {action === ProtectedAliasAction.Delete && 'protected '}aliases?
          </Dialog.Title>
          <Dialog.Description className="mt-8" data-test="delete-summary">
            {artifactMembershipCount !== undefined ? (
              `${artifactMembershipCount} instance(s) of alias "${alias}" will remain applied but will be transformed into ${
                action === ProtectedAliasAction.Add ? 'protected' : 'normal'
              } aliases.`
            ) : (
              <WaveLoader size="small" />
            )}
          </Dialog.Description>
          <div className="mt-24 flex items-center gap-8">
            <Button
              data-cy="alias-transform-confirm"
              disabled={isLoading}
              variant="primary"
              size="large"
              onClick={onConfirm}>
              Confirm
            </Button>
            <Dialog.Close asChild>
              <Button
                variant="secondary"
                size="large"
                disabled={isLoading}
                onClick={onCancel}>
                Cancel
              </Button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
